import { useState, useEffect } from "react";
import {
  collection,
  query,
  getDocs,
  where,
  onSnapshot,
  orderBy,
} from "firebase/firestore";
import { db } from "../firebase";
import { useTranslation } from "./useTranslation";
import { translatePreservingFrench } from "../utils/lang";

export const useSeriesEpisodes = (seriesId) => {
  const [episodes, setEpisodes] = useState([]);
  const [seriesData, setSeriesData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { currentLanguage, translateText } = useTranslation();

  useEffect(() => {
    if (!seriesId) {
      setEpisodes([]);
      setSeriesData(null);
      setLoading(false);
      return;
    }

    let unsubscribe;

    const fetchSeriesAndEpisodes = async () => {
      try {
        // First get the series document using the id field
        const seriesRef = collection(db, "content", "main", "series");
        const seriesQuery = query(seriesRef, where("id", "==", seriesId));
        const seriesSnapshot = await getDocs(seriesQuery);

        if (seriesSnapshot.empty) {
          console.error("No series found with id:", seriesId);
          setError("Series not found");
          setLoading(false);
          return;
        }

        // Get the series data and Firestore document ID
        const firestoreSeriesId = seriesSnapshot.docs[0].id;
        let series = {
          // Preserve existing 'id' field from data (slug), add explicit Firestore doc id
          ...seriesSnapshot.docs[0].data(),
          firestoreId: firestoreSeriesId,
        };
        // Translate series fields if needed
        if (currentLanguage !== "en") {
          const title = await (currentLanguage === "fr"
            ? translatePreservingFrench(series.title, "fr", translateText)
            : translateText(series.title, currentLanguage));
          const description = series.description
            ? await (currentLanguage === "fr"
                ? translatePreservingFrench(
                    series.description,
                    "fr",
                    translateText,
                  )
                : translateText(series.description, currentLanguage))
            : series.description;
          series = { ...series, title, description };
        }
        setSeriesData(series);

        // Now get episodes using the Firestore document ID
        const episodesRef = collection(
          db,
          "content",
          "main",
          "series",
          firestoreSeriesId,
          "episodes",
        );

        const episodesQuery = query(
          episodesRef,
          orderBy("episodeNumber", "asc"),
        );

        unsubscribe = onSnapshot(
          episodesQuery,
          (snapshot) => {
            const base = snapshot.docs.map((doc) => {
              const data = doc.data();
              return {
                // Spread data first so we can override id properly
                ...data,
                seriesId,
                // Ensure id is the Firestore document ID (not any business id field inside data)
                id: doc.id,
                firestoreId: doc.id,
                businessId: data.id || null,
              };
            });

            if (currentLanguage === "en") {
              setEpisodes(base);
            } else {
              Promise.all(
                base.map(async (ep) => {
                  const title = await (currentLanguage === "fr"
                    ? translatePreservingFrench(ep.title, "fr", translateText)
                    : translateText(ep.title, currentLanguage));
                  const shortDescription = ep.shortDescription
                    ? await (currentLanguage === "fr"
                        ? translatePreservingFrench(
                            ep.shortDescription,
                            "fr",
                            translateText,
                          )
                        : translateText(ep.shortDescription, currentLanguage))
                    : ep.shortDescription;
                  const longDescription = ep.longDescription
                    ? await (currentLanguage === "fr"
                        ? translatePreservingFrench(
                            ep.longDescription,
                            "fr",
                            translateText,
                          )
                        : translateText(ep.longDescription, currentLanguage))
                    : ep.longDescription;
                  return { ...ep, title, shortDescription, longDescription };
                }),
              )
                .then(setEpisodes)
                .catch(() => setEpisodes(base));
            }
            setLoading(false);
          },
          (err) => {
            console.error("Error fetching episodes:", err);
            setError(err.message);
            setLoading(false);
          },
        );
      } catch (err) {
        console.error("Error setting up series/episodes listener:", err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchSeriesAndEpisodes();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [seriesId, currentLanguage, translateText]);

  return { episodes, seriesData, loading, error };
};
